import type { ReactNode } from "react";
import { Button } from "@radix-ui/themes";
import { Settings2, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Box, Flex, Text } from "@/components/display/display-primitives";

interface DisplayExitOverlayProps {
  visible: boolean;
  boardName?: string;
  onClose: () => void;
  onGoToBoardSetup: () => void;
  onInteract?: () => void;
  children?: ReactNode;
}

export function DisplayExitOverlay({
  visible,
  boardName,
  onClose,
  onGoToBoardSetup,
  onInteract,
  children,
}: DisplayExitOverlayProps) {
  const { t } = useTranslation();

  if (!visible) return null;

  return (
    <Box
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 70,
        background: "rgba(0,0,0,0.56)",
        backdropFilter: "blur(6px)",
        padding: "clamp(0.9rem, 2.5vw, 1.3rem)",
      }}
      onPointerDown={(e) => {
        onInteract?.();
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <Flex
        direction="column"
        gap="3"
        style={{
          width: "min(92vw, 36rem)",
          marginLeft: "auto",
          marginRight: "auto",
          marginTop: "clamp(4vh, 8vh, 12vh)",
          borderRadius: 16,
          border: "1px solid rgba(255,255,255,0.18)",
          background: "linear-gradient(180deg, rgba(28,30,38,0.95), rgba(14,15,19,0.95))",
          boxShadow: "0 16px 54px rgba(0,0,0,0.45)",
          padding: "1rem",
        }}
        onPointerDown={(e) => {
          e.stopPropagation();
          onInteract?.();
        }}
      >
        <Flex justify="between" align="center" gap="2">
          <Flex direction="column" gap="1" style={{ minWidth: 0 }}>
            <Text as="p" size="3" style={{ margin: 0, color: "rgba(255,255,255,0.95)", fontWeight: 700 }}>
              {t("display.overlay_title")}
            </Text>
            <Text as="p" size="1" style={{ margin: 0, color: "rgba(255,255,255,0.65)" }}>
              {t("display.overlay_hint", { boardName: boardName || "" })}
            </Text>
          </Flex>

          <Button variant="ghost" color="gray" onClick={onClose} aria-label={t("display.overlay_close")}> 
            <X size={16} />
          </Button>
        </Flex>

        <Flex
          gap="2"
          direction="column"
          style={{
            alignItems: "stretch",
          }}
        >
          <Button size="3" onClick={onGoToBoardSetup} style={{ flex: 1 }}>
            <Settings2 size={16} />
            {t("display.overlay_back_to_setup")}
          </Button>
          <Button size="3" variant="soft" color="gray" onClick={onClose} style={{ minWidth: 120 }}>
            {t("display.overlay_close")}
          </Button>
        </Flex>

        {children}
      </Flex>
    </Box>
  );
}
